// lib/queryClient.ts
import { QueryClient, QueryCache, MutationCache, keepPreviousData } from '@tanstack/react-query';
import { supabase } from './supabaseClient';
import { MAINTENANCE_QUERY_KEY, isMaintenanceModeError } from '../utils/maintenance';

let isSigningOut = false;

function isSessionExpiredError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;

    const err = error as Record<string, unknown>;

    // PostgREST JWT error
    if (err.code === 'PGRST301') return true;

    // Supabase AuthError
    if (err.__isAuthError === true) return true;

    // JWT expired message
    if (typeof err.message === 'string' && err.message.includes('JWT expired')) return true;

    return false;
}

function handleGlobalError(error: unknown) {
    // The DB refused a write because maintenance switched on since the last status fetch:
    // refresh the status so AppRouter swaps in the maintenance screen.
    if (isMaintenanceModeError(error)) {
        queryClient.invalidateQueries({ queryKey: MAINTENANCE_QUERY_KEY });
        return;
    }

    if (!isSessionExpiredError(error) || isSigningOut) return;

    isSigningOut = true;
    console.warn('Session expired — signing out');
    supabase.auth.signOut().finally(() => {
        isSigningOut = false;
    });
}

export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: handleGlobalError,
    }),
    mutationCache: new MutationCache({
        onError: handleGlobalError,
    }),
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
            retry: (failureCount, error) => {
                if (isSessionExpiredError(error)) return false;
                return failureCount < 1;
            },
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            refetchOnMount: true,
            placeholderData: keepPreviousData
        },
    },
});
